import type { DateTime } from "luxon";
import { z } from "zod";

import { BackendEntityType } from "./AttachmentModel";
import type { ClubPlayersModel } from "./ClubModel";
import { baseCreateType, logoResolver } from "./CreateTypes";
import type { EntitySoccerRoles } from "./FirebaseUserRole";
import { paginationTokenResolver } from "./Search";
import type { FullTeamModel } from "./TeamModel";

export enum PendingPaymentStatusEnum {
	lastPaymentFailed = "lastPaymentFailed",
	subscriptionInProgress = "subscriptionInProgress",
	fulfilled = "fulfilled"
}

export enum PaymentRequestTypes {
	SOCCER_CLUB_PLAYER_GENERIC = "paymentRequest.soccer.club.player.generic",
	SOCCER_LEAGUE_CLUB_GENERIC = "paymentRequest.soccer.league.club.generic",
	SOCCER_COMPETITION_CLUB_GENERIC = "paymentRequest.soccer.competition.club.generic",
	SOCCER_COMPETITION_OPEN_REGISTRATION_GENERIC = "paymentRequest.soccer.competition.openRegistration.generic"
}

export enum PaymentRequestAudienceTypes {
	SOCCER_CLUB_PLAYER = "paymentRequestAudienceTypes.soccer.club.player",
	SOCCER_CLUB_MANAGER = "paymentRequestAudienceTypes.soccer.club.manager",
	SOCCER_CLUB_GENERIC = "paymentRequestAudienceTypes.soccer.club.generic",
	SOCCER_LEAGUE_PLAYER = "paymentRequestAudienceTypes.soccer.league.player",
	SOCCER_LEAGUE_MANAGER = "paymentRequestAudienceTypes.soccer.league.manager",
	SOCCER_TEAM_PLAYER = "paymentRequestAudienceTypes.soccer.team.player",
	SOCCER_TEAM_MANAGER = "paymentRequestAudienceTypes.soccer.team.manager",
	SOCCER_TEAM_GENERIC = "paymentRequestAudienceTypes.soccer.team.generic"
}

export enum StripeInvoiceStatus {
	DRAFT = "draft",
	OPEN = "open",
	PAID = "paid",
	UNCOLLECTIBLE = "uncollectible",
	VOID = "void"
}

export enum StripePaymentIntentStatus {
	REQUIRESPAYMENTMETHOD = "requires_payment_method",
	requiresconfirmation = "requires_confirmation",
	requiresaction = "requires_action",
	PROCESSING = "processing",
	SUCCEEDED = "succeeded",
	CANCELED = "canceled",
	REQUIRESCAPTURE = "requires_capture"
}

export enum ExternalProcessorPaymentStatus {
	PAID_EXTERNALLY = "paid_externally"
}

export enum PaymentOperationTypes {
	INCOME = "income",
	OUTCOME = "outcome"
}

export enum CurrencyCode {
	USD = "usd",
	HTG = "htg"
}

export enum OrderStatus {
	ACTIVE = "ACTIVE",
	CANCELED = "CANCELED",
	FAULTY = "FAULTY",
	FULFILLED = "FULFILLED",
	PROCESSING_PAYMENT = "PROCESSING_PAYMENT",
	ACTIVE_SUBSCRIPTION = "ACTIVE_SUBSCRIPTION"
}

export enum SubscriptionStatus {
	NOT_STARTED = "NOT_STARTED",
	ACTIVE = "ACTIVE",
	CANCELED = "CANCELED",
	FAULTY = "FAULTY",
	FINISHED = "FINISHED"
}

export enum PaymentStatus {
	CANCELED = "CANCELED",
	FAILED = "FAILED",
	PENDING = "PENDING",
	SUCCEEDED = "SUCCEEDED",
	PAID_EXTERNALLY = "PAID_EXTERNALLY"
}

export enum ChargeFrequency {
	DAILY = "DAILY",
	WEEKLY = "WEEKLY",
	MONTHLY = "MONTHLY",
	YEARLY = "YEARLY"
}

export enum AmountSettingTypes {
	AMOUNT = "AMOUNT",
	PERCENT = "PERCENT"
}

export enum PaymentProcessors {
	STRIPE = "STRIPE",
	PLOPPLOP = "PLOPPLOP",
	EXTERNAL = "EXTERNAL"
}

export enum PaymentSystemTypes {
	stripe = "stripe",
	stripeConnect = "stripeConnect",
	plopplop = "plopplop"
}

export const partialPaymentResolver = z.object({
	upfrontAmount: z.number(),
	numberOfInstallments: z.number(),
	frequency: z.nativeEnum(ChargeFrequency)
});

export type PartialPaymentModel = z.infer<typeof partialPaymentResolver>;

export const partialPaymentSettingsResolver = z.object({
	type: z.nativeEnum(AmountSettingTypes),
	value: z.number() // in cents
});

export type PartialPaymentSettingsModel = z.infer<typeof partialPaymentSettingsResolver>;

export const splitPaymentSettingsResolver = z.object({
	label: z.string(),
	entityId: z.string(),
	entityType: z.nativeEnum(BackendEntityType),
	split: z.object({
		value: z.number(),
		type: z.nativeEnum(AmountSettingTypes)
	})
});

export type SplitPaymentSettingsModel = z.infer<typeof splitPaymentSettingsResolver>;

export const paymentResolver = z.object({
	id: z.string(),
	ord: z.number(),
	amount: z.number(),
	currency: z.nativeEnum(CurrencyCode),
	status: z.nativeEnum(PaymentStatus),
	subscriptionId: z.string().nullable()
});

export type PaymentModel = z.infer<typeof paymentResolver>;

export const subscriptionResolver = z.object({
	id: z.string(),
	startsAt: z.string().nullable(),
	endsAt: z.string().nullable(),
	interval: z.object({
		days: z.number().optional(),
		weeks: z.number().optional(),
		months: z.number().optional(),
		years: z.number().optional()
	}),
	status: z.nativeEnum(SubscriptionStatus),
	upfrontAmount: z.number(),
	numberOfPeriods: z.number()
});

export type SubscriptionModel = z.infer<typeof subscriptionResolver>;

export const audiencesResolver = z.object({
	type: z.nativeEnum(PaymentRequestAudienceTypes),
	player: z
		.object({
			clubId: z.string().optional(),
			teamId: z.string().optional(),
			playerId: z.string().optional()
		})
		.optional(),
	club: z
		.object({
			id: z.string(),
			teams: z.array(
				z.object({
					id: z.string()
				})
			)
		})
		.optional()
});

export type AudiencesModel = z.infer<typeof audiencesResolver>;

export const createPaymentRequestObject = z.object({
	type: z.nativeEnum(PaymentRequestTypes),
	name: z.string(),
	amount: z.number(),
	currency: z.nativeEnum(CurrencyCode),
	dueDateOn: z.string(),
	objectId: z.string(),
	linkedObjectId: z.string().optional(),
	partialPayment: partialPaymentSettingsResolver.nullable(),
	feeTaxPaidByClient: z.boolean().optional(),
	audiences: z.array(audiencesResolver),
	useStripeConnect: z.boolean().optional()
});

export type CreatePaymentRequestObject = z.infer<typeof createPaymentRequestObject>;

export const linkedObjectResolver = z.object({
	id: z.string(),
	type: z.nativeEnum(BackendEntityType),
	competition: z
		.object({
			id: z.string().optional(),
			name: z.string().optional()
		})
		.optional()
});

export type LinkedObjectModel = z.infer<typeof linkedObjectResolver>;

export const paymentRequestResolver = z.object({
	id: z.string(),
	name: z.string(),
	currency: z.nativeEnum(CurrencyCode),
	orderAmount: z.number(),
	orderCount: z.number(),
	totalAmount: z.number(),
	collectedAmount: z.number().nullable(),
	collectedStripe: z.number().nullable(),
	collectedExternally: z.number().nullable(),
	audiences: z.array(audiencesResolver).optional(),
	dueDateOn: z.string(),
	// partial: z.array(partialPaymentResolver),
	partial: partialPaymentSettingsResolver.optional().nullable(),
	linkedObject: linkedObjectResolver.optional().nullable(),
	removedAt: z.string().nullable().optional(),
	parentRequestId: z.string().nullable().optional(),
	feeTaxPaidByClient: z.boolean().optional(),
	relatedPaymentRequests: z.array(z.lazy(() => paymentRequestResolver)).optional()
});

export type PaymentRequestModel = z.infer<typeof paymentRequestResolver>;

export const paymentRequestOrderBaseResolver = z.object({
	id: z.string(),
	name: z.string(),
	amount: z.number(),
	currency: z.nativeEnum(CurrencyCode),
	createdAt: z.string(),
	dueDate: z.string(),
	partial: partialPaymentSettingsResolver.optional().nullable(),
	feeTaxPaidByClient: z.boolean().optional(),
	split: z
		.object({
			label: z.string(),
			objectId: z.string(),
			objectType: z.nativeEnum(BackendEntityType),
			amount: z.number().nullable(),
			percent: z.number().nullable()
		})
		.array()
		.optional(),
	paymentLink: z.string().optional().nullable(),
	subscription: subscriptionResolver.optional().nullable(),
	status: z.nativeEnum(OrderStatus).optional().nullable()
});

export type PaymentRequestOrderBaseModal = z.infer<typeof paymentRequestOrderBaseResolver>;

export const paymentRequestOrderResolver = z.object({
	id: z.string(),
	amount: z.number(),
	currency: z.nativeEnum(CurrencyCode),
	lastTransactionId: z.string().nullable(),
	paidAmount: z.number(),
	refundedAmount: z.number(),
	paidAt: z.string().nullable(),
	status: z.nativeEnum(OrderStatus).nullable(),
	subject: z.object({
		id: z.string(),
		image: logoResolver.nullable().optional(),
		name: z.string()
	})
});

export type PaymentRequestOrderModel = z.infer<typeof paymentRequestOrderResolver>;

export const cardObject = z.object({
	number: z.string(),
	expMonth: z.number(),
	expYear: z.number(),
	cvc: z.string()
});

export type CardObject = z.infer<typeof cardObject>;

export const paymentTransactionResolver = z.object({
	amount: z.number(),
	createdAt: z.string(),
	platformCommissionAmount: z.number(),
	processorCommissionAmount: z.number(),
	status: z
		.nativeEnum(StripeInvoiceStatus)
		.or(z.nativeEnum(StripePaymentIntentStatus))
		.or(z.nativeEnum(ExternalProcessorPaymentStatus)),
	payoutId: z.string().nullable().optional(),
	transferId: z.string().nullable().optional(),
	topupId: z.string().nullable().optional(),
	paymentMethod: z
		.object({
			id: z.string(),
			brand: z.string(),
			card: z.object({
				expMonth: z.number(),
				expYear: z.number(),
				last4: z.string()
			})
		})
		.nullable(),
	paymentRequest: paymentRequestResolver.pick({ id: true, name: true, currency: true }).nullable(),
	processor: z.nativeEnum(PaymentProcessors).optional()
});

export type PaymentTransactionModel = z.infer<typeof paymentTransactionResolver>;

export const shortPaymentTransactionResolver = paymentRequestOrderResolver
	.pick({
		id: true,
		amount: true
	})
	.extend({
		status: z
			.nativeEnum(StripeInvoiceStatus)
			.or(z.nativeEnum(StripePaymentIntentStatus))
			.or(z.nativeEnum(ExternalProcessorPaymentStatus)),
		platformCommissionAmount: z.number(),
		processorCommissionAmount: z.number(),
		transferId: z.string().nullable(),
		topupId: z.string().nullable(),
		payoutId: z.string().nullable(),
		dateAt: z.string(),
		paymentOrderId: z.string().nullable(),
		paymentOrderCurrency: z.string().nullable(),
		transferOrderId: z.string().nullable(),
		transferOrderCurrency: z.string().nullable(),
		operationType: z.nativeEnum(PaymentOperationTypes)
	});

export type ShortPaymentTransactionModel = z.infer<typeof shortPaymentTransactionResolver>;

export const paymentAccountStripeBalanceResolver = z.object({
	available: z.array(
		z.object({
			amount: z.number(),
			currency: z.string()
		})
	),
	connectReserved: z.array(
		z.object({
			amount: z.number(),
			currency: z.string()
		})
	),
	instantAvailable: z.array(
		z.object({
			amount: z.number(),
			currency: z.string()
		})
	),
	issuing: z.array(
		z.object({
			amount: z.number(),
			currency: z.string()
		})
	),
	pending: z.array(
		z.object({
			amount: z.number(),
			currency: z.string()
		})
	)
});

export type PaymentAccountStripeBalanceModel = z.infer<typeof paymentAccountStripeBalanceResolver>;

export const paymentAccountMetricsResolver = z.object({
	inbound: z.object({
		currentPeriod: z.number(),
		previousPeriod: z.number()
	}),
	outbound: z.object({
		currentPeriod: z.number(),
		previousPeriod: z.number()
	}),
	balance: z.object({
		total: z.number(),
		previousPeriod: z.number(),
		stripeBalance: paymentAccountStripeBalanceResolver.nullable()
	}),
	currencies: z.lazy(() =>
		paymentAccountMetricsResolver
			.omit({ currencies: true })
			.extend({
				currency: z.nativeEnum(CurrencyCode)
			})
			.array()
	)
});

export type PaymentAccountMetricsModel = z.infer<typeof paymentAccountMetricsResolver>;

export const pendingPaymentRequestOrderResolver = paymentRequestOrderResolver
	.pick({ id: true, amount: true, currency: true, status: true })
	.extend({
		onBehalfOf: baseCreateType
			.pick({ id: true, name: true })
			.extend({
				avatar: logoResolver.nullable(),
				clubName: z.string()
			})
			.nullable(),
		paymentLink: z.string().nullable().optional(),
		paymentRequest: z.object({
			id: z.string(),
			name: z.string(),
			createdAt: z.string(),
			dueDateOn: z.string(),
			type: z.nativeEnum(PaymentRequestTypes).optional(),
			linkedObjectType: z.string().nullable().optional(),
			partial: partialPaymentSettingsResolver.nullable().optional(),
			teamsCounts: z.number().optional(),
			feeTaxPaidByClient: z.boolean().optional(),
			object: z.object({
				id: z.string(),
				type: z.nativeEnum(BackendEntityType),
				league: z
					.object({
						name: z.string()
					})
					.optional(),
				name: z.string().nullable().optional()
			}),
			linkedObject: linkedObjectResolver.optional()
		}),
		payments: paymentResolver.array(),
		subscription: subscriptionResolver.nullable()
	});

export type PendingPaymentRequestOrderModel = z.infer<typeof pendingPaymentRequestOrderResolver>;

export interface PaymentFieldsForm {
	for: string;
	amount: number;
	currency: CurrencyCode;

	// TODO: move to enum after we know all opts
	unit: string;

	dueDate: DateTime;
	tax?: number;

	partialPayment: null | PartialPaymentSettingsModel;

	audienceType: BackendEntityType | EntitySoccerRoles;
	selectedValues: Partial<FullTeamModel>[] | Partial<ClubPlayersModel>[];
	isAllSelected?: boolean;

	feeTaxPaidByClient?: boolean;
}

export const stripeConnectAccountResolver = z.object({
	id: z.string(),
	email: z.string().nullable(),
	type: z.string(),
	detailsSubmitted: z.boolean(),
	chargesEnabled: z.boolean(),
	payoutsEnabled: z.boolean(),
	transfersEnabled: z.boolean()
});

export type StripeConnectAccountModel = z.infer<typeof stripeConnectAccountResolver>;

export const stripeConnectAccountIntegrationResponseResolver = z.object({
	stripeAccount: stripeConnectAccountResolver.nullable()
});

export type StripeConnectAccountIntegrationResponse = z.infer<typeof stripeConnectAccountIntegrationResponseResolver>;

export const paymentOrgInfoResolver = z.object({
	entityId: z.string(),
	entityType: z.nativeEnum(BackendEntityType),
	name: z.string(),
	logo: logoResolver.nullable(),
	accountId: z.string()
});

export type PaymentOrgInfoModel = z.infer<typeof paymentOrgInfoResolver>;

export const listPaymentOrgInfoResolver = z.object({
	organizations: paymentOrgInfoResolver.array(),
	nextToken: paginationTokenResolver
});

export type ListPaymentOrgInfoResponse = z.infer<typeof listPaymentOrgInfoResolver>;
